import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { OTSInformation } from '@1po/1po-bff-fe-spec/generated/catalog/vehicle/response/GetVehicleOTSResponse';
import { Table } from 'antd-v5';
import type { ColumnsType } from 'antd-v5/es/table';
import { DotIcon } from 'assets/icons';
import { getOtsInformation } from 'domains/catalog/Catalog.store';
import { Container, Flex, MarginBox, Text } from 'UI';
import { Icon } from 'UI/Icon';
import { getSearchData } from 'utils';
import { getOtsTypeColor } from './ots.utils';

export const OTSTabContent = () => {
  const { t } = useTranslation();
  const otsInformation = useSelector(getOtsInformation);
  const items = getSearchData(otsInformation) ?? [];

  const otsTypeToLabel = (otsType: string | undefined): string => {
    switch (otsType) {
      case 'BLUE':
        return t('catalog.ots.type.blue', 'Bleu');
      case 'RED':
        return t('catalog.ots.type.red', 'Rouge');
      default:
        return otsType ?? '';
    }
  };

  const agreementLabel = (value: string) => {
    switch (value) {
      case '0':
        return t('common.no', 'No');
      case '1':
        return t('common.yes', 'Yes');
      default:
        return '';
    }
  };

  const columns: ColumnsType<OTSInformation> = [
    {
      title: t('catalog.ots.table.type', 'Type'),
      key: 'otsType',
      dataIndex: 'otsType',
      align: 'center',
      render: (otsType: string) => (
        <Flex align={'center'} justify={'center'} gap={8}>
          <Icon IconComponent={DotIcon} size={10} noPointer color={getOtsTypeColor(otsType)} display={'block'} />
          <Text type={'text'}>{otsTypeToLabel(otsType)}</Text>
        </Flex>
      ),
    },
    {
      title: t('catalog.ots.table.ots_number', 'OTS Number'),
      key: 'code',
      dataIndex: 'code',
      align: 'center',
    },
    {
      title: t('catalog.ots.table.ots_label', 'OTS Label'),
      key: 'description',
      dataIndex: 'description',
      align: 'center',
    },
    {
      title: t('catalog.ots.table.technical_note_number', 'Technical note number'),
      key: 'technicalNoteNumber',
      dataIndex: 'technicalNoteNumber',
      align: 'center',
    },
    {
      title: t('catalog.ots.table.mandatory_approval', 'Mandatory approval'),
      key: 'agreement',
      dataIndex: 'agreement',
      align: 'center',
      render: (agreement) => agreementLabel(agreement),
    },
  ];

  return (
    <Container>
      <MarginBox mt={30}>
        <Text type="heading3">
          {items.length === 1
            ? t('catalog.ots.header.single', 'This vehicle is currently under OTS.')
            : t('catalog.ots.header', 'This vehicle is currently under one or several vehicle special operation(s)')}
        </Text>
      </MarginBox>
      <Flex maxWidth={1400}>
        <MarginBox mt={20} mb={100}>
          <Table<OTSInformation> columns={columns} dataSource={items} rowKey={'code'} pagination={false} />
        </MarginBox>
      </Flex>
    </Container>
  );
};
